import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ShieldCheck, ShieldAlert } from 'lucide-react';
import type { AuditLogDto, Paginated } from '@workforce/shared';
import { api, qs } from '@/lib/api';
import { useTz } from '@/lib/timezone';
import { describeAction } from '@/lib/format';
import { t } from '@/lib/i18n';
import { Button, Card, EmptyState, ErrorBanner, Field, Input, PageHeader, Pagination, Select, Spinner } from '@/components/ui';
import { ChangeSummary } from './EmployeeDetail';

const ACTION_GROUPS = [
  { value: '', label: 'All actions' },
  { value: 'auth.', label: 'Sign-ins' },
  { value: 'employee.', label: 'Employee changes' },
  { value: 'account.', label: 'Login accounts' },
  { value: 'leave.', label: 'Leave requests & balances' },
  { value: 'leave_', label: 'Leave types & policies' },
  { value: 'setting.', label: 'Settings' },
  { value: 'schedule.', label: 'Schedules' },
  { value: 'holiday', label: 'Holidays' },
  { value: 'holiday_calendar.synced', label: 'Official holiday syncs' },
  { value: 'department.', label: 'Departments' },
];

export function AuditLogPage() {
  const { fmt } = useTz();
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const q = useQuery({
    queryKey: ['audit', { action, actor, from, to, page }],
    queryFn: () => api.get<Paginated<AuditLogDto>>(`/audit${qs({ action, actor, from, to, page, pageSize: 30 })}`),
    placeholderData: keepPreviousData,
  });
  const [verify, setVerify] = useState<null | { ok: boolean; checked: number; brokenAt: string | null }>(null);
  const [verifying, setVerifying] = useState(false);

  return (
    <div>
      <PageHeader
        title={t('Audit log')}
        subtitle={t('Append-only record of every important action. Entries cannot be edited or deleted.')}
        actions={
          <Button
            variant="secondary"
            loading={verifying}
            icon={<ShieldCheck className="h-4 w-4" />}
            onClick={async () => {
              setVerifying(true);
              try {
                setVerify(await api.get('/audit/verify'));
              } finally {
                setVerifying(false);
              }
            }}
          >
            {t('Verify integrity')}
          </Button>
        }
      />
      {verify && (
        <div className={`mb-4 flex items-center gap-2 rounded-lg border px-4 py-3 text-sm ${verify.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-red-50 text-red-900'}`}>
          {verify.ok ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
          {verify.ok
            ? t('Hash chain intact — {n} entries verified. No entry has been altered or removed.', { n: verify.checked })
            : t('Integrity check FAILED at entry #{id}. The log has been modified outside the application.', { id: verify.brokenAt })}
        </div>
      )}
      <div className="card mb-4 grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t('Action')}>
          <Select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}>
            {ACTION_GROUPS.map((a) => (
              <option key={a.value} value={a.value}>
                {t(a.label)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('Done by')}>
          <Input value={actor} onChange={(e) => { setActor(e.target.value); setPage(1); }} placeholder={t('e-mail')} />
        </Field>
        <Field label={t('From')}>
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
        </Field>
        <Field label={t('To')}>
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
        </Field>
      </div>
      <ErrorBanner error={q.error} />
      <Card bodyClass="p-0">
        {q.isLoading ? (
          <Spinner />
        ) : !q.data?.items.length ? (
          <EmptyState title={t('No entries match')} />
        ) : (
          <>
            <ul className="divide-y divide-slate-100">
              {q.data.items.map((r) => (
                <li key={r.id} className="px-5 py-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm">
                      <span className="font-medium text-slate-900">{describeAction(r.action)}</span>
                      {r.subjectEmployeeId && (
                        <>
                          {' · '}
                          {r.subjectEmployeeName ? (
                            <Link to={`/employees/${r.subjectEmployeeId}`} className="text-brand-700 hover:underline">
                              {r.subjectEmployeeName}
                            </Link>
                          ) : (
                            <span className="text-slate-500">{t('deleted employee')}</span>
                          )}
                        </>
                      )}
                      {!r.subjectEmployeeId && r.entityId && r.entityType === 'setting' && <span className="text-slate-500"> · {r.entityId}</span>}
                    </p>
                    <p className="text-xs text-slate-500">
                      #{r.id} · {fmt(r.occurredAt, null, 'dd LLL yyyy, HH:mm:ss')} · {r.actorLabel}
                      {r.ip ? ` · ${r.ip}` : ''}
                    </p>
                  </div>
                  <ChangeSummary oldValue={r.oldValue} newValue={r.newValue} />
                  {r.reason && <p className="mt-1 text-xs text-slate-600">{t('Reason: {reason}', { reason: r.reason })}</p>}
                </li>
              ))}
            </ul>
            <Pagination page={page} pageSize={30} total={q.data.total} onPage={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}
